import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { BarChart3 } from 'lucide-react';
import { usePartnerAuthStore, getApiErrorMessage } from '@/stores/partnerAuthStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const { login, isLoggingIn } = usePartnerAuthStore();
  const navigate = useNavigate();

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');

    // Mirrors PartnerLoginRequest: email is required and must be an email,
    // password is required. Checked here so an empty submit never costs one
    // of the ten attempts the login throttle allows per minute.
    const nextErrors: { email?: string; password?: string } = {};
    if (!email.trim()) nextErrors.email = 'Email is required.';
    else if (!EMAIL_PATTERN.test(email.trim())) nextErrors.email = 'Enter a valid email address.';
    if (!password) nextErrors.password = 'Password is required.';
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      await login(email, password);
      navigate('/growth');
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
  }

  return (
    <div className="flex min-h-screen">
      <div className="flex flex-1 flex-col items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-6">
          <div className="flex justify-center">
            <img src="/logo-dark.png" alt="KashRound Logo" className="w-40" />
          </div>
          <div className="text-center">
            <h1 className="font-display text-2xl font-bold">KashRound Partners</h1>
            <p className="text-muted-foreground">Sign in to the back office.</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="email" required>Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@kashround.com"
                value={email}
                aria-invalid={!!fieldErrors.email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (fieldErrors.email) setFieldErrors((prev) => ({ ...prev, email: undefined }));
                }}
                required
              />
              {fieldErrors.email && <p className="text-xs text-destructive">{fieldErrors.email}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" required>Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="Your password"
                value={password}
                aria-invalid={!!fieldErrors.password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (fieldErrors.password) setFieldErrors((prev) => ({ ...prev, password: undefined }));
                }}
                required
              />
              {fieldErrors.password && <p className="text-xs text-destructive">{fieldErrors.password}</p>}
            </div>
            {error && <p className="text-xs text-destructive">{error}</p>}
            <Button type="submit" variant="accent" className="w-full" disabled={isLoggingIn}>
              {isLoggingIn ? 'Logging in...' : 'Log in'}
            </Button>
          </form>
        </div>
      </div>
      <div
        className="hidden lg:flex lg:flex-1 lg:items-center lg:justify-center p-12"
        style={{ background: 'var(--gradient-hero)' }}
      >
        <div className="max-w-md text-center">
          <div className="mb-8 flex justify-center">
            <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-accent shadow-accent">
              <BarChart3 className="h-12 w-12 text-accent-foreground" />
            </div>
          </div>
          <h2 className="font-display text-3xl font-bold text-primary-foreground">Insights across every group</h2>
          <p className="mt-4 text-lg text-primary-foreground/80">
            Track subscriptions, revenue and adoption across the whole platform.
          </p>
        </div>
      </div>
    </div>
  );
}
